import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { calculateGeoQualityScore } from "@/lib/geo-quality-score";
import { getCreatorTierFromScore, CreatorTier } from "@/components/User/CreatorTierBadge";
import { formatDistanceToNow } from "date-fns";

interface CreatorStats {
  totalPrompts: number;
  totalCopies: number;
  avgGeoScore: number;
  creatorTier: CreatorTier;
  lastActive: string;
  loading: boolean;
}

export function useCreatorStats(userId: string): CreatorStats {
  const [stats, setStats] = useState<CreatorStats>({
    totalPrompts: 0,
    totalCopies: 0,
    avgGeoScore: 0,
    creatorTier: "emerging",
    lastActive: "recently",
    loading: true
  });

  useEffect(() => {
    const fetchStats = async () => {
      if (!userId) {
        setStats(prev => ({ ...prev, loading: false }));
        return;
      }

      try {
        // Fetch user's prompts with all data needed for GEO scoring
        const { data: prompts, error } = await supabase
          .from("prompts")
          .select("id, title, description, content, category, geo_metadata, copy_count, created_at")
          .eq("user_id", userId)
          .order("created_at", { ascending: false });

        if (error) {
          console.error("Error fetching prompts for stats:", error);
          setStats(prev => ({ ...prev, loading: false }));
          return;
        }

        const totalPrompts = prompts?.length || 0;
        const totalCopies = prompts?.reduce((sum, p) => sum + (p.copy_count || 0), 0) || 0;

        // Calculate average GEO score
        let avgGeoScore = 0;
        if (prompts && prompts.length > 0) {
          const scores = prompts.map(prompt => {
            const score = calculateGeoQualityScore({
              title: prompt.title || "",
              description: prompt.description || "",
              content: prompt.content || "",
              category: prompt.category || "",
              geoMetadata: prompt.geo_metadata as any
            });
            return score.score;
          });
          avgGeoScore = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
        }

        // Calculate creator tier
        const creatorTier = getCreatorTierFromScore(avgGeoScore, totalPrompts);

        // Calculate last active from most recent prompt
        let lastActive = "recently";
        if (prompts && prompts.length > 0 && prompts[0].created_at) {
          lastActive = formatDistanceToNow(new Date(prompts[0].created_at), { addSuffix: false });
        }

        setStats({
          totalPrompts,
          totalCopies,
          avgGeoScore,
          creatorTier,
          lastActive,
          loading: false
        });
      } catch (error) {
        console.error("Error calculating creator stats:", error);
        setStats(prev => ({ ...prev, loading: false }));
      }
    };

    fetchStats();
  }, [userId]);

  return stats;
}
